import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { HeardOfDialog } from "./HeardOfDialog";
import { SentimentButtons } from "../left-panel/SentimentButtons";
import type { MSHatData, HatSentiment } from "../../types";

interface CenterPanelProps {
  hat: MSHatData;
  heardOf?: boolean | null;
  onHeardOfChange?: (heardOf: boolean) => void;
  onFlip?: () => void;
  clusterColor?: string;
  hatSentiment?: HatSentiment;
  onHatSentimentChange?: (sentiment: HatSentiment) => void;
}

const PanelContainer = styled(Box, {
  label: "CenterPanelContainer",
})(() => ({
  height: "100%",
  aspectRatio: "368 / 569",
  flexShrink: 0,
  position: "relative",
}));

const CardContainer = styled(Box, {
  label: "HatCardContainer",
  shouldForwardProp: (prop) => prop !== "borderColor",
})<{ borderColor?: string }>(({ borderColor }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 16,
  overflow: "hidden",
  border: borderColor ? `4px solid ${borderColor}` : "none",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
}));

const CardImage = styled("img", {
  label: "HatCardImage",
})(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
}));

const GradientOverlay = styled(Box, {
  label: "HatCardGradient",
})(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.9) 20%, transparent 60%)",
  pointerEvents: "none",
}));

const FlipButton = styled(Box, {
  label: "HatCardFlipButton",
})(() => ({
  position: "absolute",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

const SentimentPill = styled(Box, {
  label: "SentimentPill",
})(() => ({
  position: "absolute",
  top: 24,
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "rgba(216, 216, 216, 0.65)",
  borderRadius: 40,
  padding: "8px 32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
}));

const FlipLabel = styled(Typography, {
  label: "FlipButtonLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

const FlipIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M17 1l4 4-4 4"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 11V9a4 4 0 0 1 4-4h14"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 23l-4-4 4-4"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 13v2a4 4 0 0 1-4 4H3"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CenterPanel = ({
  hat,
  heardOf,
  onHeardOfChange,
  onFlip,
  clusterColor,
  hatSentiment,
  onHatSentimentChange,
}: CenterPanelProps) => {
  const imageUrl = hat.hatCard?.largeCard || hat.displayImage?.url;

  return (
    <PanelContainer>
      <CardContainer borderColor={clusterColor}>
        {imageUrl && <CardImage src={imageUrl} alt={hat.displayName} />}
        <GradientOverlay />

        <SentimentPill>
          <SentimentButtons
            sentiment={hatSentiment}
            onChange={onHatSentimentChange}
          />
        </SentimentPill>

        <FlipButton onClick={onFlip}>
          <FlipIcon />
          <FlipLabel>Flip</FlipLabel>
        </FlipButton>

        {heardOf === null && onHeardOfChange && (
          <HeardOfDialog onAnswer={onHeardOfChange} />
        )}
      </CardContainer>
    </PanelContainer>
  );
};
